import CakeOutlined from '@mui/icons-material/CakeOutlined';
import { useTheme } from '@mui/material/styles';
import styled from 'styled-components';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { IAbsenteeismRateItem } from '../../../interfaces/IAbsenteeismRate';
import { AgeRangeTick } from './AgeRangeMedicalCertificateChart';
import { Card } from './AbsenteeismRateCard.styles';
import { AbsenteeismRateCardTitle } from './AbsenteeismRateCard';
import { chartColorsFor, chartMutedTextColor } from './chartColors';

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const ChartArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 240px;
`;

const ChartFill = styled.div`
  position: absolute;
  inset: 0;
`;

export const RankAgeRangeWithMoreMedicalCertificate = ({ data }: { data: IAbsenteeismRateItem[] }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';

  const barColors = chartColorsFor(isDark);
  const mutedText = chartMutedTextColor(isDark);

  const chartData = data.map((item, index) => ({
    ...item,
    percentage: Math.min(Math.max(Number(item.percentage) || 0, 0), 100),
    color: barColors[index % barColors.length],
  }));

  return (
    <ChartCard $isDark={isDark}>
      <AbsenteeismRateCardTitle
        icon={<CakeOutlined />}
        title="Taxa de absenteísmo por faixa etária"
        subtitle="Colaboradores com atestado sobre o total da faixa etária"
      />

      <ChartArea>
        <ChartFill>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 32, right: 8, bottom: 16, left: -12 }}>
              <CartesianGrid vertical={false} stroke={palette.divider} strokeWidth={1} />

              <XAxis
                dataKey="label"
                interval={0}
                tickLine={false}
                axisLine={{ stroke: palette.divider }}
                tick={(tickProps: any) => <AgeRangeTick {...tickProps} fill={mutedText} />}
                height={44}
              />

              <YAxis
                type="number"
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={value => `${value}%`}
                tick={{ fontSize: 12, fill: mutedText }}
                tickLine={false}
                axisLine={false}
              />

              <Tooltip
                cursor={{ fill: palette.action.hover }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const item = payload[0].payload as IAbsenteeismRateItem;
                  return (
                    <div
                      style={{
                        background: palette.background.paper,
                        border: `1px solid ${palette.divider}`,
                        borderRadius: 8,
                        padding: '10px 14px',
                        fontSize: 13,
                        color: palette.text.primary,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      }}
                    >
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{item.label}</p>
                      <p style={{ margin: '6px 0 0', color: mutedText }}>
                        <strong style={{ color: palette.text.primary }}>{item.percentage}%</strong> ·{' '}
                        {item.withCertificate} de {item.total} colaboradores
                      </p>
                    </div>
                  );
                }}
              />

              <Bar dataKey="percentage" name="Taxa de absenteísmo" barSize={24} maxBarSize={24} radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="percentage"
                  position="top"
                  formatter={(value: number) => (value > 0 ? `${value}%` : '')}
                  style={{ fontSize: 11, fontWeight: 700, fill: palette.text.primary }}
                />
                {chartData.map(item => (
                  <Cell key={item.label} fill={item.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartFill>
      </ChartArea>
    </ChartCard>
  );
};
